// İletişim doğrulamasını UI bileşenlerinden ayırarak tüm formlarda aynı kuralları kullanır.
const TURKISH_NAME_LETTERS = "A-Za-zÇĞİÖŞÜÂÎÛçğıöşüâîû"
const NAME_TOKEN_PATTERN = new RegExp(
  `^[${TURKISH_NAME_LETTERS}]+(?:['’-][${TURKISH_NAME_LETTERS}]+)*$`
)
const REPEATED_CHARACTER_PATTERN = new RegExp(
  `^([${TURKISH_NAME_LETTERS}])\\1+$`,
  "i"
)
const EXCESSIVE_CHARACTER_REPEAT_PATTERN = /(.)\1{2,}/iu
const EMAIL_LOCAL_PATTERN = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+$/i
const EMAIL_DOMAIN_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i
const EMAIL_TLD_PATTERN = /^[a-z]{2,24}$/i

const PLACEHOLDER_VALUES = new Set([
  "aaa",
  "aaaa",
  "asdf",
  "deneme",
  "isim",
  "qwe",
  "qwerty",
  "soyisim",
  "test",
  "xxx",
])

const emptyResult = () => ({ errors: {}, isValid: true })

const invalidResult = (field, message) => ({
  errors: { [field]: message },
  isValid: false,
})

const isEmpty = (value) => String(value ?? "").trim() === ""

const isPlaceholder = (value) => PLACEHOLDER_VALUES.has(value.toLocaleLowerCase("tr-TR"))

const validateNameTokens = (value) => {
  const tokens = value.split(" ")

  return tokens.every((token) => {
    const lettersOnly = token.replace(/['’-]/g, "")

    return (
      lettersOnly.length >= 2 &&
      NAME_TOKEN_PATTERN.test(token) &&
      !REPEATED_CHARACTER_PATTERN.test(lettersOnly) &&
      !EXCESSIVE_CHARACTER_REPEAT_PATTERN.test(lettersOnly) &&
      !isPlaceholder(lettersOnly)
    )
  })
}

export const normalizeName = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")

export const normalizeEmail = (value) =>
  String(value ?? "")
    .trim()
    .toLocaleLowerCase("en-US")

export const normalizeTurkishMobile = (value) => {
  const compactValue = String(value ?? "")
    .trim()
    .replace(/[\s()-]/g, "")

  if (/^\+905\d{9}$/.test(compactValue)) {
    return `0${compactValue.slice(3)}`
  }

  if (/^905\d{9}$/.test(compactValue)) {
    return `0${compactValue.slice(2)}`
  }

  if (/^5\d{9}$/.test(compactValue)) {
    return `0${compactValue}`
  }

  return compactValue
}

export const validateName = (
  value,
  { field = "name", label = "İsim", required = true } = {}
) => {
  const normalizedValue = normalizeName(value)

  if (!required && isEmpty(normalizedValue)) return emptyResult()
  if (isEmpty(normalizedValue)) return invalidResult(field, `${label} zorunludur.`)

  if (!validateNameTokens(normalizedValue)) {
    return invalidResult(
      field,
      `${label} yalnızca geçerli harflerden oluşmalı ve tekrar eden anlamsız değerler içermemelidir.`
    )
  }

  return emptyResult()
}

export const validateFullName = (
  value,
  { field = "name", label = "Ad soyad", required = true } = {}
) => {
  const normalizedValue = normalizeName(value)

  if (!required && isEmpty(normalizedValue)) return emptyResult()
  if (isEmpty(normalizedValue)) return invalidResult(field, `${label} zorunludur.`)

  if (normalizedValue.split(" ").length < 2) {
    return invalidResult(field, `${label} en az iki kelimeden oluşmalıdır.`)
  }

  if (!validateNameTokens(normalizedValue)) {
    return invalidResult(
      field,
      `${label} yalnızca geçerli harflerden oluşmalı ve tekrar eden anlamsız değerler içermemelidir.`
    )
  }

  return emptyResult()
}

export const validateEmail = (
  value,
  { field = "email", label = "E-posta", required = true } = {}
) => {
  const normalizedValue = normalizeEmail(value)

  if (!required && isEmpty(normalizedValue)) return emptyResult()
  if (isEmpty(normalizedValue)) return invalidResult(field, `${label} zorunludur.`)

  if (
    normalizedValue.length > 254 ||
    normalizedValue.includes("..") ||
    /\s/.test(normalizedValue)
  ) {
    return invalidResult(field, "Geçerli bir e-posta adresi giriniz.")
  }

  const parts = normalizedValue.split("@")
  if (parts.length !== 2) {
    return invalidResult(field, "Geçerli bir e-posta adresi giriniz.")
  }

  const [localPart, domain] = parts
  const domainLabels = domain.split(".")
  const tld = domainLabels[domainLabels.length - 1] ?? ""
  const hasValidLocalPart =
    localPart.length > 0 &&
    localPart.length <= 64 &&
    !localPart.startsWith(".") &&
    !localPart.endsWith(".") &&
    EMAIL_LOCAL_PATTERN.test(localPart)
  const hasValidDomain =
    domainLabels.length >= 2 &&
    domainLabels.every((domainLabel) => EMAIL_DOMAIN_LABEL_PATTERN.test(domainLabel)) &&
    EMAIL_TLD_PATTERN.test(tld)

  if (!hasValidLocalPart || !hasValidDomain) {
    return invalidResult(field, "Geçerli bir e-posta adresi giriniz.")
  }

  return emptyResult()
}

export const validateTurkishMobile = (
  value,
  { field = "phone", label = "Telefon", required = true } = {}
) => {
  const normalizedValue = normalizeTurkishMobile(value)

  if (!required && isEmpty(normalizedValue)) return emptyResult()
  if (isEmpty(normalizedValue)) return invalidResult(field, `${label} zorunludur.`)

  if (!/^05\d{9}$/.test(normalizedValue)) {
    return invalidResult(
      field,
      "Telefon 05 ile başlayan 11 haneli bir cep telefonu numarası olmalıdır."
    )
  }

  return emptyResult()
}

const NORMALIZERS = {
  email: normalizeEmail,
  fullName: normalizeName,
  name: normalizeName,
  phone: normalizeTurkishMobile,
}

const VALIDATORS = {
  email: validateEmail,
  fullName: validateFullName,
  name: validateName,
  phone: validateTurkishMobile,
}

export const validateContactFields = (values, rules) => {
  const normalizedValues = { ...values }
  const errors = {}

  Object.entries(rules).forEach(([field, rule]) => {
    const { type, ...options } = rule
    const normalize = NORMALIZERS[type]
    const validate = VALIDATORS[type]

    if (!normalize || !validate) {
      throw new Error(`Desteklenmeyen iletişim doğrulama tipi: ${type}`)
    }

    normalizedValues[field] = normalize(values[field])
    const result = validate(normalizedValues[field], { ...options, field })
    Object.assign(errors, result.errors)
  })

  return {
    values: normalizedValues,
    errors,
    isValid: Object.keys(errors).length === 0,
  }
}
